import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PRODUCT_TYPES } from '../lib/utils';
import {
  Bookmark,
  Plus,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  FileText,
  Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reservas() {
  const navigate = useNavigate();
  const { canCreate } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [createForm, setCreateForm] = useState({
    product_type: '',
    family_code: '',
    quantity: 5,
    notes: ''
  });

  const [useForm, setUseForm] = useState({
    description: '',
    unit: 'UN',
    notes: ''
  });

  useEffect(() => {
    if (!canCreate()) {
      navigate('/dashboard');
      return;
    }
    fetchReservations();
  }, [canCreate, navigate]);

  // Fetch families/clients when product type changes (same as CriarCodigo)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        let url = `${API_URL}/api/admin/categories/available`;
        if (createForm.product_type) {
          url += `?type_code=${createForm.product_type}`;
        }
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Combine clients and families into a single list
        const combined = [
          ...response.data.clients.map(c => ({ ...c, type: 'cliente' })),
          ...response.data.families.map(f => ({ ...f, type: 'familia' }))
        ];
        setFamilies(combined);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    
    fetchCategories();
  }, [createForm.product_type]);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reservations`);
      setReservations(response.data);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();
    if (!createForm.product_type || !createForm.family_code) {
      toast.error('Selecione o tipo e a família');
      return;
    }

    setFormLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/reservations`, {
        product_type: parseInt(createForm.product_type),
        family_code: parseInt(createForm.family_code),
        quantity: createForm.quantity,
        notes: createForm.notes || null
      });
      
      toast.success(`${response.data.codes.length} códigos reservados com sucesso!`);
      setShowCreateDialog(false);
      setCreateForm({ product_type: '', family_code: '', quantity: 5, notes: '' });
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar reserva');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUseCode = async (e) => {
    e.preventDefault();
    if (!useForm.description) {
      toast.error('Preencha a descrição');
      return;
    }

    setFormLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/reservations/from-reservation`, {
        reservation_code_id: selectedCode.id,
        description: useForm.description,
        unit: useForm.unit,
        notes: useForm.notes || null
      });
      
      toast.success(`Código ${response.data.code} criado com sucesso!`);
      setShowUseDialog(false);
      setUseForm({ description: '', unit: 'UN', notes: '' });
      setSelectedCode(null);
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar código');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteReservation = async () => {
    try {
      await axios.delete(`${API_URL}/api/reservations/${selectedReservation.id}`);
      toast.success('Reserva excluída com sucesso!');
      setShowDeleteDialog(false);
      setSelectedReservation(null);
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir reserva');
    }
  };

  const handleReleaseCode = async (reservation, code) => {
    try {
      await axios.post(`${API_URL}/api/reservations/${reservation.id}/release/${code.id}`);
      toast.success('Código liberado!');
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao liberar código');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reserved':
        return <Badge className="bg-blue-100 text-blue-700 text-xs"><Clock className="w-3 h-3 mr-1" />Reservado</Badge>;
      case 'used':
        return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Utilizado</Badge>;
      case 'released':
        return <Badge className="bg-slate-100 text-slate-500 text-xs"><XCircle className="w-3 h-3 mr-1" />Liberado</Badge>;
      default:
        return null;
    }
  };

  const getAvailableCount = (codes) => codes.filter(c => c.status === 'reserved').length;

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="reservas-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Reserva de Códigos
            </h1>
            <p className="text-slate-500 mt-1">
              Reserve códigos sequenciais para seus desenhos de engenharia
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm"
            data-testid="new-reservation-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Nova Reserva
          </Button>
        </div>

        {/* Reservations List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        ) : reservations.length === 0 ? (
          <Card className="border-slate-200 shadow-none">
            <CardContent className="py-12 text-center">
              <Bookmark className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
              <p className="text-slate-500">Nenhuma reserva encontrada</p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                variant="outline"
                className="mt-4"
              >
                Criar primeira reserva
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reservations.map((reservation) => (
              <Card key={reservation.id} className="border-slate-200 shadow-none">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
                        <Bookmark className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                        {reservation.product_type_name} - {reservation.family_name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Reservado em {new Date(reservation.reserved_at).toLocaleDateString('pt-BR')}
                        {reservation.notes && ` • ${reservation.notes}`}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                        {getAvailableCount(reservation.codes)} disponíveis
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedReservation(reservation);
                          setShowDeleteDialog(true);
                        }}
                        className="h-8 w-8 text-slate-400 hover:text-red-600"
                        data-testid={`delete-reservation-${reservation.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[180px]">Código</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reservation.codes.map((code) => (
                        <TableRow key={code.id} className="border-b border-slate-100">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="material-code text-sm bg-slate-100 px-2 py-1 rounded">
                                {code.code}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(code.code)}
                                className="h-6 w-6"
                              >
                                <Copy className="w-3 h-3" strokeWidth={1.5} />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(code.status)}
                          </TableCell>
                          <TableCell className="text-right">
                            {code.status === 'reserved' && (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedReservation(reservation);
                                    setSelectedCode(code);
                                    setShowUseDialog(true);
                                  }}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`use-code-${code.id}`}
                                >
                                  <FileText className="w-3 h-3 mr-1" strokeWidth={1.5} />
                                  Usar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReleaseCode(reservation, code)}
                                  className="text-slate-600"
                                >
                                  Liberar
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Reservation Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                Nova Reserva de Códigos
              </DialogTitle>
              <DialogDescription>
                Reserve códigos sequenciais para usar nos seus desenhos
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateReservation} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Tipo de Produto
                </Label>
                <Select
                  value={createForm.product_type}
                  onValueChange={(v) => setCreateForm({ ...createForm, product_type: v, family_code: '' })}
                >
                  <SelectTrigger data-testid="reservation-product-type">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRODUCT_TYPES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <span className="font-mono text-slate-500 mr-2">{key}</span>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Família / Cliente
                </Label>
                <Select
                  value={createForm.family_code}
                  onValueChange={(v) => setCreateForm({ ...createForm, family_code: v })}
                  disabled={!createForm.product_type}
                >
                  <SelectTrigger data-testid="reservation-family-code">
                    <SelectValue placeholder="Selecione a família ou cliente" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {families.map((f) => (
                      <SelectItem key={f.code} value={f.code.toString()}>
                        <span className="font-mono text-slate-500 mr-2">{f.code.toString().padStart(3, '0')}</span>
                        {f.name}
                        <span className="text-xs text-slate-400 ml-2">
                          ({f.type === 'cliente' ? 'Cliente' : 'Família'})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Quantidade de Códigos
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={createForm.quantity === '' ? '' : createForm.quantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCreateForm({ ...createForm, quantity: val === '' ? '' : parseInt(val) || '' });
                  }}
                  onBlur={(e) => {
                    // Restore default value if empty on blur
                    if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                      setCreateForm({ ...createForm, quantity: 1 });
                    }
                  }}
                  className="h-10"
                  data-testid="reservation-quantity"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Observação (opcional)
                </Label>
                <Input
                  placeholder="Ex: Projeto Cofre NCR 2024"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="h-10"
                  data-testid="reservation-notes"
                />
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="ghost" onClick={() => setShowCreateDialog(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={formLoading}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="create-reservation-submit"
                >
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bookmark className="w-4 h-4 mr-2" />}
                  Reservar Códigos
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Use Code Dialog */}
        <Dialog open={showUseDialog} onOpenChange={setShowUseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                Usar Código Reservado
              </DialogTitle>
              <DialogDescription>
                Cadastre o material usando o código{' '}
                <code className="material-code bg-slate-100 px-2 py-0.5 rounded">
                  {selectedCode?.code}
                </code>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUseCode} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Descrição do Material *
                </Label>
                <Textarea
                  placeholder="Ex: Parafuso Allen M8x20 Aço Inox"
                  value={useForm.description}
                  onChange={(e) => setUseForm({ ...useForm, description: e.target.value })}
                  className="min-h-[80px]"
                  data-testid="use-code-description"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Unidade
                  </Label>
                  <Select
                    value={useForm.unit}
                    onValueChange={(v) => setUseForm({ ...useForm, unit: v })}
                  >
                    <SelectTrigger data-testid="use-code-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UN">UN - Unidade</SelectItem>
                      <SelectItem value="PC">PC - Peça</SelectItem>
                      <SelectItem value="KG">KG - Quilograma</SelectItem>
                      <SelectItem value="M">M - Metro</SelectItem>
                      <SelectItem value="M2">M² - Metro Quadrado</SelectItem>
                      <SelectItem value="L">L - Litro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Observação
                  </Label>
                  <Input
                    placeholder="Opcional"
                    value={useForm.notes}
                    onChange={(e) => setUseForm({ ...useForm, notes: e.target.value })}
                    className="h-10"
                    data-testid="use-code-notes"
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="ghost" onClick={() => setShowUseDialog(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={formLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="use-code-submit"
                >
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Criar Material
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Reservation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">
                Excluir Reserva
              </AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir esta reserva? Os códigos não utilizados serão liberados.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteReservation}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-reservation"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
